import React, { useState } from 'react'
import { useLocation, matchPath, useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import { useRef, useEffect } from 'react'
import Typography from '../../common/Typography/index'
import Button from '../../common/Button/index'
import { IoCloudDownloadOutline } from 'react-icons/io5'
import { isAuthenticated, logout } from '../../../shared/utils/authUtils.js'
import { isNew } from '../../../shared/utils/ageUtils.js'
import Input from '../../common/Input/Input.jsx'
// import { Calendar } from 'lucide-react'

const MiniHeader = ({ title, onEdit, onApprove, onDecline, onRemove, data = [], onExport, onFilterChange, signOut }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const isPropertyDetails = matchPath('/properties/:propertyId', location.pathname)
  const isSettings = matchPath('/settings', location.pathname)
  const isHelpPage = matchPath('/help/*', location.pathname)
  const isHelpFAQ = matchPath('/help/faq', location.pathname)
  const isHelpContact = matchPath('/help/contact', location.pathname)
  
  const [authToken, setAuthToken] = useState(false)
  const { fromTab, property } = location.state || {}

  useEffect(() => {
    setAuthToken(isAuthenticated())
    const handleStorageChange = () => {
      setAuthToken(isAuthenticated())
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  const [activeFilter, setActiveFilter] = useState('all')
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: new Date(),
  })
  const datePickerRef = useRef(null)

  // Format date for display
  const formatDate = date => {
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    })
  }

  // Handle date range selection
  const handleDateRangeSelect = (start, end) => {
    const newDateRange = { startDate: start, endDate: end }
    setDateRange(newDateRange)
    setShowDatePicker(false)
    setActiveFilter('custom')

    // Call onFilterChange if provided to notify parent component
    if (onFilterChange) {
      onFilterChange(data, newDateRange)
    }
  }

  // Set date range based on filter
  const handleFilterClick = filter => {
    const today = new Date()
    let startDate = new Date()
    let endDate = new Date()

    switch (filter) {
      case 'all':
        // Show all data without date filtering
        setActiveFilter(filter)
        if (onFilterChange) {
          onFilterChange(data, null) // null means no date filter
        }
        return
      case 'today':
        // Start and end date are already today
        break
      case 'week':
        startDate = new Date(today)
        startDate.setDate(today.getDate() - today.getDay()) // Start of week (Sunday)
        endDate = new Date(startDate)
        endDate.setDate(startDate.getDate() + 6) // End of week (Saturday)
        break
      case 'month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1) // First day of month
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0) // Last day of month
        break
      case 'custom':
        // Don't change dates, just open the picker
        setShowDatePicker(true)
        setActiveFilter(filter)
        return
      default:
        break
    }

    const newDateRange = { startDate, endDate }
    setDateRange(newDateRange)
    setActiveFilter(filter)

    // Call onFilterChange if provided to notify parent component
    if (onFilterChange) {
      onFilterChange(data, newDateRange)
    }
  }

  // Handle help navigation
  const handleHelpNavigation = (route) => {
    navigate(route)
  }

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Export data to CSV based on current page
  const exportToCSV = () => {
    if (onExport) {
      const pageData = onExport(dateRange)
      if (pageData === undefined) {
        // Only alert if pageData is completely undefined
        alert('Export functionality not available for this page')
        return
      }
    } else {
      alert('Export functionality not available for this page')
    }
  }

  const DateRangePicker = () => {
    const [startDate, setStartDate] = useState(dateRange.startDate)
    const [endDate, setEndDate] = useState(dateRange.endDate)

    const handleApply = () => {
      handleDateRangeSelect(startDate, endDate)
    }

    return (
      <div
        className="absolute right-20 top-16 bg-white shadow-lg rounded-lg p-4 border border-gray-200 z-50"
        ref={datePickerRef}
      >
        <div className="flex gap-4 mb-4">
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              id="startDate"
              name="startDate"
              type="date"
              className="border border-gray-300 rounded-md p-2"
              value={startDate.toISOString().split('T')[0]}
              onChange={e => setStartDate(new Date(e.target.value))}
            />
          </div>
          <div>
            <label htmlFor="EndDate" className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <Input
              id="EndDate"
              name="EndDate"
              type="date"
              className="border border-gray-300 rounded-md p-2"
              value={endDate.toISOString().split('T')[0]}
              onChange={e => setEndDate(new Date(e.target.value))}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="small" onClick={() => setShowDatePicker(false)}>
            Cancel
          </Button>
          <Button size="small" onClick={handleApply}>
            Apply
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="ml-[260px] top-20 mt-4 z-30 bg-white rounded-2xl shadow h-auto">
      <div className="flex justify-between">
        <div className="p-4">
          <Typography variant="h4" className="text-xl items-center">
            {title}
          </Typography>
        </div>
        
        {/* Help page buttons */}
        {isHelpPage && (
          <div className="flex gap-2 mr-4 py-3">
            <Button
              size="small"
              variant={isHelpFAQ ? 'primary' : 'secondary'}
              onClick={() => handleHelpNavigation('/help/faq')}
              className={isHelpFAQ ? 'text-teal-600 border-teal-600' : 'bg-teal-600 text-white hover:bg-teal-700'}
            >
              FAQ
            </Button>
            <Button
              size="small"
              variant={isHelpContact ? 'primary' : 'secondary'}
              onClick={() => handleHelpNavigation('/help/contact')}
              className={isHelpContact ? 'text-teal-600 border-teal-600' : 'bg-teal-600 text-white hover:bg-teal-700'}
            >
              Contact Us
            </Button>
          </div>
        )}

        {!isPropertyDetails && !isSettings && !isHelpPage && (
          <div className="flex gap-2 mr-4 py-3">
            <Button
              size="small"
              variant={activeFilter === 'all' ? 'primary' : 'secondary'}
              onClick={() => handleFilterClick('all')}
            >
              All
            </Button>
            <Button
              size="small"
              variant={activeFilter === 'today' ? 'primary' : 'secondary'}
              onClick={() => handleFilterClick('today')}
            >
              Today
            </Button>
            <Button
              variant={activeFilter === 'week' ? 'primary' : 'secondary'}
              size="small"
              onClick={() => handleFilterClick('week')}
            >
              This Week
            </Button>
            <Button
              variant={activeFilter === 'month' ? 'primary' : 'secondary'}
              size="small"
              onClick={() => handleFilterClick('month')}
            >
              This Month
            </Button>
            <Button
              variant={activeFilter === 'custom' ? 'primary' : 'secondary'}
              size="small"
              onClick={() => handleFilterClick('custom')}
              className="flex items-center gap-1"
            >
              {/* <Calendar size={16} /> */}
              {activeFilter === 'custom'
                ? `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`
                : 'Year Range'}
            </Button>
            <div>
              <Button size="small" to="/reports" className="flex items-center gap-2">
                <IoCloudDownloadOutline />
                Download
              </Button>
            </div>
          </div>
        )}

        {/* Property details buttons */}
        {isPropertyDetails && (
          <div className="flex gap-4 py-3 px-6">
            {property?.status === 'DECLINED' && (fromTab === 'decline' || fromTab === 'all') && (
              <Button size="medium" variant="secondary" className="border-red-600">
                <Typography className="text-danger">Declined</Typography>
              </Button>
            )}

            {property?.status === 'LIVE' && (fromTab === 'live' || fromTab === 'all') && (
              <Button size="medium" className="px-8" onClick={onEdit}>
                Edit
              </Button>
            )}
            {property?.status === 'ARCHIVED' && (
              <Button size="medium" className="px-8" onClick={onRemove}>
                Remove
              </Button>
            )}
            {fromTab === 'all' && (isNew(property?.createdAt) || property?.status === 'PENDING') && (
              <>
                <Button size="medium" className="px-8" onClick={onApprove}>
                  Approve
                </Button>
                <Button size="medium" variant="danger" className="px-8" onClick={onDecline}>
                  Decline
                </Button>
              </>
            )}
          </div>
        )}

        {/* Settings buttons */}
        {isSettings && (
          <div className="flex py-3 px-6">
            {authToken && (
              <Button variant="danger" onClick={signOut || (() => {
                logout()
                setAuthToken(false)
                navigate('/login')
              })}>
                Signout
              </Button>
            )}
          </div>
        )}
      </div>
      {showDatePicker && <DateRangePicker />}
    </div>
  )
}

MiniHeader.propTypes = {
  title: PropTypes.string.isRequired,
  onEdit: PropTypes.func,
  onApprove: PropTypes.func,
  onDecline: PropTypes.func,
  onRemove: PropTypes.func,
  data: PropTypes.array,
  onExport: PropTypes.func,
  onFilterChange: PropTypes.func,
  signOut: PropTypes.func,
}

export default MiniHeader